import { IconButton, InputAdornment } from '@material-ui/core'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import lodashDebounce from 'lodash/debounce'
import { BaseTheme, CloseOutlineIcon, SearchOutlineIcon, styled, TextField } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { useCallback, useEffect, useMemo, useState } from 'react'

const DEFAULT_DEBOUNCE = 500
const SearchField = styled(TextField, { name: 'SearchField' })(({ theme }) => ({
  margin: 0,
  width: 400,
  marginRight: 10,
  '& .OSUI-InputBase-root': {
    backgroundColor: `${theme.palette.neutralGrey[100]}`,
  },
}))

const ClearButton = styled(IconButton)(({ theme }) => ({
  padding: 4,
  backgroundColor: 'transparent',
}))

type SearchInputProps = {
  setFilters: (filters: Record<string, any>) => void
  filterValues: Record<string, any>
  width?: number | string
  maxWidth?: number | string
  placeholder?: string
  resetListPage?: () => void
}

const SearchInput = (props: SearchInputProps) => {
  const { setFilters, filterValues, width, maxWidth, placeholder, resetListPage } = props
  const translate = useTranslate()
  const [searchText, setSearchText] = useState(filterValues?.q || '')

  useEffect(() => {
    if (filterValues?.q !== searchText) {
      setSearchText(filterValues?.q || '')
    }
  }, [filterValues?.q])

  const updateFiltersAndLog = useCallback(
    (queryVal) => {
      if (resetListPage) resetListPage()
      setFilters({ ...filterValues, q: queryVal })
      logAmplitudeEvent('generic_filter_interacted', {
        action: 'updated',
        key: 'q',
      })
    },
    [setFilters, filterValues, resetListPage]
  )

  const debouncedUpdate = useMemo(() => {
    return lodashDebounce(updateFiltersAndLog, DEFAULT_DEBOUNCE)
  }, [updateFiltersAndLog])

  const handleSearch = (e) => {
    const queryVal = e?.target?.value
    setSearchText(queryVal)
    debouncedUpdate(queryVal)
  }

  const handleClearSearch = () => {
    setSearchText('')
    updateFiltersAndLog('')
  }

  return (
    <SearchField
      label={false}
      size="small"
      value={searchText}
      sx={{
        ...(width && { width }),
        ...(maxWidth && { maxWidth }),
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchOutlineIcon color={BaseTheme.palette.grey[700]} />
          </InputAdornment>
        ),
        endAdornment: searchText ? (
          <InputAdornment position="end">
            <ClearButton size="small" onClick={handleClearSearch}>
              <CloseOutlineIcon size={10} color={BaseTheme.palette.grey[700]} />
            </ClearButton>
          </InputAdornment>
        ) : null,
      }}
      onChange={handleSearch}
      variant="outlined"
      placeholder={translate(placeholder ?? 'Search')}
    />
  )
}

export default SearchInput
